/**
 * Inventory (M6, permission-gated INVENTORY_MANAGE) — read-only stock
 * view: the catalog parts list with each part's on-hand level. Stock is
 * a per-part fetch; rows render "—" while loading / when uninitialised.
 * Searchable by label / SKU. Tablet-friendly rows.
 */
import { useMemo, useState } from 'react'
import { Plus } from '@phosphor-icons/react'
import { useCatalogParts, useStock } from '@/hooks/useStaff'
import { partLabel } from '@/apis/staffApis'
import { useI18n } from '@/i18n/TranslationContext'
import { PermissionGate } from '@/auth/PermissionGate'
import { FEATURE_TECH_PART_REQUESTS } from '@/featureFlags'
import { MaterialReorderModal } from './MaterialReorderModal'
import type { CatalogPartResponse } from '@/apis/types'

export function InventoryRoute() {
  const { t, lang } = useI18n()
  const [search, setSearch] = useState('')
  const [reorderOpen, setReorderOpen] = useState(false)
  const { data, isLoading, isError } = useCatalogParts()

  const parts = useMemo(() => {
    const all = data?.content ?? []
    const q = search.trim().toLowerCase()
    if (!q) return all
    return all.filter(
      (p) => partLabel(p, lang).toLowerCase().includes(q) || p.sku.toLowerCase().includes(q),
    )
  }, [data, search, lang])

  return (
    <PermissionGate requires={['inventory.manage']}>
      <section aria-label={t.inventory.title}>
        <div className="mb-px14 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="mb-1.5 font-display text-26 font-extrabold tracking-mk-neg5 text-glass-text">
              {t.inventory.title}
            </h1>
            <p className="text-13 tracking-mk-p1 text-glass-text-muted">{t.inventory.subtitle}</p>
          </div>
          {/* Reorder files INVENTORY-kind part requests through the same
              endpoint the task +Part button used, so it goes with it (see
              featureFlags.ts). The stock list below stays — reading what is
              on the shelf was never the part a technician initiates. */}
          {FEATURE_TECH_PART_REQUESTS && (
            <button
              type="button"
              onClick={() => setReorderOpen(true)}
              className="mt-0.5 inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-accent px-4 py-2 text-13 font-bold text-white transition hover:bg-accent-hover"
            >
              <Plus size={16} weight="bold" aria-hidden="true" />
              <span className="hidden sm:inline">{t.reorder.button}</span>
            </button>
          )}
        </div>

        {FEATURE_TECH_PART_REQUESTS && (
          <MaterialReorderModal open={reorderOpen} onClose={() => setReorderOpen(false)} />
        )}

        {/* Sticky so the search stays reachable while the list scrolls. It
            pins just under the sticky topbar; the negative inset + padding
            extend the opaque backdrop to the full content width so rows
            scroll cleanly underneath. */}
        <div className="sticky top-[60px] z-10 -mx-6 mb-3 bg-glass-base px-6 pb-2 pt-1">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.inventory.search}
            className="w-full rounded-sm border border-glass-border bg-glass-fill px-3.5 py-px11 text-13 text-glass-text placeholder:text-glass-text-muted focus:border-accent focus:outline-none"
          />
        </div>

        {isLoading ? (
          <div role="status" className="py-12 text-center text-sm text-glass-text-muted">{t.home.loading}</div>
        ) : isError ? (
          <div role="alert" className="py-12 text-center text-sm text-glass-text-muted">{t.inventory.loadError}</div>
        ) : parts.length === 0 ? (
          <div className="rounded-md border border-glass-border bg-glass-fill p-px18 text-center text-sm text-glass-text-muted">
            {t.inventory.empty}
          </div>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {parts.map((p) => (
              <PartRow key={p.id} part={p} />
            ))}
          </ul>
        )}
      </section>
    </PermissionGate>
  )
}

function PartRow({ part }: { part: CatalogPartResponse }) {
  const { t, lang } = useI18n()
  const { data: stock, isLoading } = useStock(part.id)

  const onHand = isLoading ? '…' : stock ? String(stock.onHand) : '—'
  const low = stock?.belowReorderThreshold ?? false

  return (
    <li className="flex items-center gap-3 rounded-md border border-glass-border bg-glass-fill p-px14">
      <div className="min-w-0 flex-1">
        <div className="truncate text-13-5 font-bold text-glass-text">{partLabel(part, lang)}</div>
        <div className="mt-0.5 text-11-5 text-glass-text-muted">SKU {part.sku}</div>
      </div>
      {low && (
        <span className="flex-shrink-0 rounded-full bg-accent/10 px-2.5 py-1 text-11 font-bold uppercase tracking-mk-p2 text-accent">
          {t.inventory.lowStock}
        </span>
      )}
      {/* No price column. A part carries none — what it sells for belongs
          to the delivery it comes off, and this screen has no notion of
          deliveries. Showing a single figure here told a technician one
          number while the job billed another. */}
      <div className="min-w-12 flex-shrink-0 text-right">
        <div className="font-display text-20 font-extrabold tracking-mk-neg3 text-glass-text">{onHand}</div>
        <div className="text-10-5 uppercase tracking-mk-up12 text-glass-text-muted">{t.inventory.onHand}</div>
      </div>
    </li>
  )
}
